import AutoCompleteField, { VField } from '../../components/Fields'
import { API_URL, CLASSI_DI_PRECISIONE, TIPI_BILANCIA, TIPI_BILANCIA_EM } from '../../constants'
import { Button } from 'react-bootstrap'
import { useAuth } from '../../auth'
import { fetchApiJson } from '@inmagik/react-crud'
import { FieldArray, getIn, useFormikContext } from 'formik'
import { useMemo } from 'react'
import { useIsAmministratore, useIsSupervisore } from '../../utils/utils'

// Sezioni del form, mostrate una alla volta nella creazione a passaggi su mobile (ModalWizardBilancia).
// fields: campi validati prima di passare al passaggio successivo
export const BILANCIA_STEPS = [
  {
    title: 'Anagrafica',
    fields: [
      'tipo_bilancia',
      'matricola',
      'cliente',
      'sede',
      'sigla',
      'nome_produttore',
      'autoprodotta',
      'dismessa',
      'fuori_uso',
      'data_scadenza_periodica_da_db_precedente',
    ],
  },
  {
    title: 'Omologazione',
    fields: ['verif_periodica', 'cert_approv', 'data_collaudo', 'anno_marcatura_ce'],
  },
  {
    title: 'Dati tecnici',
    fields: [
      'tipo_bilancia_em',
      'rivendita',
      'rivenditore',
      'dimensioni',
      'tipo_terminale',
      'div_principale',
      'div_secondaria',
      'div_terziaria',
      'unita_di_misura',
      'portata_massima',
      'portata_massima_secondaria',
      'portata_massima_terziaria',
      'classe_di_precisone',
      'portata_minima',
      'portata_minima_secondaria',
      'portata_minima_terziaria',
      'lunghezza_cavo',
      'rif_welmec',
      'zona_utilizzo',
      'reparto',
      'pesa_interrata',
    ],
  },
  {
    title: 'Celle e installazione',
    fields: [
      'tipo_celle',
      'numero_celle',
      'portata_celle',
      'matricole_celle_content',
      'collegamenti_seriali',
      'note',
      'data_installazione',
      'nome_installatore',
      'rapporto_taratura',
      'periodo_controllo_qualita',
      'data_scadenza_taratura_da_db_precedente',
    ],
  },
]

// Senza step mostra tutto il form; con step solo la sezione corrispondente di BILANCIA_STEPS
export default function FormBilancia({ step }: { step?: number }) {
  const { tokens } = useAuth()
  const { values, setFieldValue } = useFormikContext()
  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()
  const clienteValue = useMemo(() => {
    if (getIn(values, 'cliente')) {
      return getIn(values, 'cliente')
    }
    if (getIn(values, 'cliente_pk')) {
      return getIn(values, 'cliente_pk')
    }
    return ''
  }, [values])

  const show = (n: number) => step === undefined || step === n

  return (
    <>
      {step === undefined && (
        <div className="row">
          <div className="col-md-12 mb-3">
            <div className="w-100 bg-secondary-body p-1 d-flex align-items-center">
              <h5 className="heading-h5 mb-0">Anagrafica bilancia</h5>
            </div>
          </div>
        </div>
      )}
      {show(0) && (
        <>
          <div className="row align-items-end">
            <div className="col-12 col-md-4">
              <VField name="tipo_bilancia" label="Tipo bilancia" required as="select">
                <option value="">Seleziona un tipo</option>
                {TIPI_BILANCIA.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </VField>
            </div>
            <div className="col-12 col-md-4">
              <VField name="matricola" label="Matricola" required />
            </div>
            <div className="col-12 col-md-4">
              {!getIn(values, 'id') && (
                <Button
                  type="button"
                  className="mb-3"
                  disabled={!getIn(values, 'tipo_bilancia')}
                  title={getIn(values, 'tipo_bilancia') ? undefined : 'Seleziona prima il tipo bilancia'}
                  onClick={async () => {
                    const data = await fetchApiJson(
                      API_URL + '/bilance/bilancia/get-next-matricola?tipo_bilancia=' + getIn(values, 'tipo_bilancia'),
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    setFieldValue('matricola', data.matricola)
                    setFieldValue('matricola_generata', true)
                  }}
                  variant="outline-secondary"
                >
                  Genera matricola
                </Button>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <AutoCompleteField
                name="cliente"
                url={`${API_URL}/bilance/cliente/options`}
                onChange={(value) => {
                  setFieldValue('cliente', value)
                  setFieldValue('sede', undefined)
                }}
                label="Cliente"
                required
                keyQuery="options-cliente"
                value={clienteValue}
              />
            </div>
            <div className="col-md-6">
              <AutoCompleteField
                name="sede"
                url={`${API_URL}/bilance/sede/options`}
                label="Sede"
                required
                params={{
                  cliente: clienteValue,
                }}
                keyQuery="options-sede"
                isDisabled={!getIn(values, 'cliente') && !getIn(values, 'cliente_pk')}
                value={getIn(values, 'sede')}
              />
            </div>
            <div className="col-md-3">
              <VField name="sigla" label="Sigla" />
            </div>
            <div className="col-md-6">
              <VField name="nome_produttore" label="Nome produttore" required />
            </div>
            {isAdmin && (
              <div className="col-md-3">
                <VField name="autoprodotta" type="checkbox" label="Autoprodotta" />
              </div>
            )}
            <div className="col-md-3">
              <VField name="dismessa" type="checkbox" label="Dismessa" />
            </div>
            <div className="col-md-3">
              <VField name="fuori_uso" type="checkbox" label="Fuori uso" />
            </div>
            {(isAdmin || isSupervisore) && getIn(values, 'numero_interventi_sistema_nuovo') === 0 && (
              <div className="col-md-3">
                <VField
                  name="data_scadenza_periodica_da_db_precedente"
                  label="Forza prossima scadenza"
                  as="input"
                  type="date"
                />
              </div>
            )}
          </div>
        </>
      )}
      {show(1) && (
        <div className="row align-items-end">
          <div className="row align-items-end">
            <div className="col-md-3">
              <VField name="verif_periodica" label="Strumento omologato" as="select">
                <option value="">Seleziona</option>
                <option value="true">Si</option>
                <option value="false">No</option>
              </VField>
            </div>
            <div className="col-md-3">
              <VField name="cert_approv" label="Provvedimento" />
            </div>
            <div className="col-md-3">
              <VField name="data_collaudo" label="Data collaudo" as="input" type="date" />
            </div>
            <div className="col-md-3">
              <VField name="anno_marcatura_ce" label="Anno marcatura CE" as="input" />
            </div>
          </div>
        </div>
      )}
      {show(2) && (
        <div className="row align-items-end">
          {step === undefined && (
            <div className="col-md-12 mb-3">
              <div className="w-100 bg-secondary-body p-1 d-flex align-items-center">
                <h5 className="heading-h5 mb-0">Dati tecnici</h5>
              </div>
            </div>
          )}
          <div className="col-md-4">
            <VField name="tipo_bilancia_em" label="Tipo bilancia" as="select" required>
              <option value="">Seleziona un tipo</option>
              {TIPI_BILANCIA_EM.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </VField>
          </div>
          <div className="col-md-2">
            <VField name="rivendita" label="Rivendita" type="checkbox" />
          </div>
          <div className="col-md-5">
            <VField name="rivenditore" label="Rivenditore" />
          </div>
          <div className="col-md-3">
            <VField name="dimensioni" label="Dimensioni" />
          </div>
          <div className="col-md-4">
            <VField name="tipo_terminale" label="Tipo terminale" />
          </div>
          <div className="col-md-12">
            <h6 className="heading-h6">Divisioni</h6>
          </div>
          <div className="col-md-3">
            <VField name="div_principale" label="Divisione 1" />
          </div>
          <div className="col-md-3">
            <VField name="div_secondaria" label="Divisione 2" />
          </div>
          <div className="col-md-3">
            <VField name="div_terziaria" label="Divisione 3" />
          </div>
          <div className="col-md-3">
            <VField name="unita_di_misura" label="Unità di misura" as="select">
              <option value="">Seleziona un'unità</option>
              <option value="kg">kg</option>
              <option value="g">g</option>
            </VField>
          </div>

          <div className="col-md-3">
            <VField name="portata_massima" label="Portata massima 1" />
          </div>
          <div className="col-md-3">
            <VField name="portata_massima_secondaria" label="Portata massima 2" />
          </div>
          <div className="col-md-3">
            <VField name="portata_massima_terziaria" label="Portata massima 3" />
          </div>

          <div className="col-md-3">
            <VField name="classe_di_precisone" label="Classe di precisione" as="select">
              <option value="">Seleziona una classe</option>
              {CLASSI_DI_PRECISIONE.map((classe) => (
                <option key={classe.value} value={classe.value}>
                  {classe.label}
                </option>
              ))}
            </VField>
          </div>
          <div className="col-md-3">
            {getIn(values, 'verif_periodica') !== 'true' && getIn(values, 'verif_periodica') !== true ? (
              <VField name="portata_minima" label="Portata minima" />
            ) : (
              <VField
                name="portata_minima"
                label="Portata minima"
                as="select"
                disabled={!getIn(values, 'classe_di_precisone') || !getIn(values, 'div_principale')}
              >
                <option value="">Seleziona una portata</option>

                {(() => {
                  const classe = getIn(values, 'classe_di_precisone')
                  const divPrincipale = getIn(values, 'div_principale')?.replace(',', '.')

                  if (!divPrincipale) return null

                  const multipliers = {
                    I: [100],
                    II: [20, 50],
                    III: [20, 50],
                    IIII: [10],
                  } as Record<string, number[]>

                  return multipliers[classe]?.map((multiplier) => (
                    <option key={multiplier} value={divPrincipale * multiplier}>
                      {divPrincipale * multiplier}
                    </option>
                  ))
                })()}
              </VField>
            )}
          </div>

          <div className="col-md-3">
            {getIn(values, 'verif_periodica') !== 'true' && getIn(values, 'verif_periodica') !== true ? (
              <VField name="portata_minima_secondaria" label="Portata minima 2" />
            ) : (
              <VField
                name="portata_minima_secondaria"
                label="Portata minima 2"
                as="select"
                disabled={!getIn(values, 'classe_di_precisone') || !getIn(values, 'div_secondaria')}
              >
                <option value="">Seleziona una portata</option>

                {(() => {
                  const classe = getIn(values, 'classe_di_precisone')
                  const divSecondaria = getIn(values, 'div_secondaria')?.replace(',', '.')

                  if (!divSecondaria) return null

                  const multipliers = {
                    I: [100],
                    II: [20, 50],
                    III: [20, 50],
                    IIII: [10],
                  } as Record<string, number[]>

                  return multipliers[classe]?.map((multiplier) => (
                    <option key={multiplier} value={divSecondaria * multiplier}>
                      {divSecondaria * multiplier}
                    </option>
                  ))
                })()}
              </VField>
            )}
          </div>
          <div className="col-md-3">
            {getIn(values, 'verif_periodica') !== 'true' && getIn(values, 'verif_periodica') !== true ? (
              <VField name="portata_minima_terziaria" label="Portata minima 3" />
            ) : (
              <VField
                name="portata_minima_terziaria"
                label="Portata minima 3"
                as="select"
                disabled={!getIn(values, 'classe_di_precisone') || !getIn(values, 'div_terziaria')}
              >
                <option value="">Seleziona una portata</option>

                {(() => {
                  const classe = getIn(values, 'classe_di_precisone')
                  const divTerziaria = getIn(values, 'div_terziaria')?.replace(',', '.')

                  if (!divTerziaria) return null

                  const multipliers = {
                    I: [100],
                    II: [20, 50],
                    III: [20, 50],
                    IIII: [10],
                  } as Record<string, number[]>

                  return multipliers[classe]?.map((multiplier) => (
                    <option key={multiplier} value={divTerziaria * multiplier}>
                      {divTerziaria * multiplier}
                    </option>
                  ))
                })()}
              </VField>
            )}
          </div>
          <div className="col-md-3">
            <VField name="lunghezza_cavo" label="Lunghezza cavo" />
          </div>
          <div className="col-md-3">
            <VField name="rif_welmec" label="Rif. Welmec" />
          </div>
          <div className="col-md-3">
            <VField name="zona_utilizzo" label="Zona utilizzo" />
          </div>
          <div className="col-md-6">
            <VField name="reparto" label="Reparto" />
          </div>
          <div className="col-md-3">
            <VField name="pesa_interrata" label="Pesa interrata" type="checkbox" />
          </div>
        </div>
      )}
      {show(3) && (
        <>
          <div className="row align-items-end">
            <div className="col-md-12">
              <h6 className="heading-h6">Dati celle</h6>
            </div>
            <div className="col-md-7">
              <VField name="tipo_celle" label="Tipo" />
            </div>
            <div className="col-md-2">
              <VField name="numero_celle" label="Numero" />
            </div>
            <div className="col-md-3">
              <VField name="portata_celle" label="Portata" />
            </div>

            <FieldArray
              name="matricole_celle_content"
              render={(arrayHelpers) => (
                <>
                  {/* cicle numero_celle times */}
                  {Array.from({ length: getIn(values, 'numero_celle') }).map((_, index) => (
                    <div className="col-md-4">
                      <VField name={`matricole_celle_content.${index}`} label={`Matricola cella ${index + 1}`} />
                    </div>
                  ))}
                </>
              )}
            />
            <div className="col-md-12">
              <VField name="collegamenti_seriali" label="Collegamenti seriali" />
            </div>
            <div className="col-md-12">
              <VField name="note" label="Note" as="textarea" />
            </div>
            <div className="col-md-3">
              <VField
                name="data_installazione"
                label="Data installazione"
                as="input"
                type="date"
                disabled={getIn(values, 'id') && getIn(values, 'data_installazione') ? true : false}
              />
            </div>
            <div className="col-md-6">
              <VField name="nome_installatore" label="Collaudatore" />
            </div>
          </div>

          <div className="row align-items-end">
            <div className="col-md-3">
              <VField name="rapporto_taratura" label="Rapporto taratura" as="input" type="checkbox" />
            </div>
            <div className="col-md-3">
              <VField name="periodo_controllo_qualita" label="Cadenza (mesi)" />
            </div>
            {(isAdmin || isSupervisore) && getIn(values, 'numero_interventi_sistema_nuovo_taratura') === 0 && (
              <div className="col-md-3">
                <VField
                  name="data_scadenza_taratura_da_db_precedente"
                  label="Forza prossima scadenza taratura"
                  as="input"
                  type="date"
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
